import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { API_CONFIG, ApiConfig } from '../api-config';

export interface ServiceStatusSelectOption {
  value: string;
  label: string;
}

/**
 * Estatus disponibles para un servicio.
 * GET {apiUrl}/selects/service-statuses?serviceId={uuid|prefix}
 * → { success, data: [{ id, text }] }
 *
 * `serviceId` acepta UUID de services.id o prefix de negocio (ej. giros).
 * Cache por clave; invalidar al abrir drawers o tras alta de estatus
 * para no reutilizar un listado vacío/stale.
 */
@Injectable({ providedIn: 'root' })
export class ServiceStatusesSelectService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);
  private readonly cache = new Map<string, ServiceStatusSelectOption[]>();
  private readonly endpointUrl = '/selects/service-statuses';

  /** @param serviceKey UUID del servicio o prefix (ej. `giros`) */
  getOptions(serviceKey: string): Observable<ServiceStatusSelectOption[]> {
    const key = serviceKey.trim();
    if (!key) return of([]);

    const cached = this.cache.get(key);
    if (cached) return of(cached);

    return this.httpClient
      .get(`${this.apiConfig.apiUrl}${this.endpointUrl}`, {
        params: { serviceId: key },
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map((resp) => this.parseSelectResponse(resp.body)),
        tap((options) => this.cache.set(key, options)),
        catchError(() => of([])),
      );
  }

  /** Invalida caché de un servicio (por id o prefix) o de todos. */
  invalidate(serviceKey?: string): void {
    if (!serviceKey?.trim()) {
      this.cache.clear();
      return;
    }
    this.cache.delete(serviceKey.trim());
  }

  private parseSelectResponse(body: unknown): ServiceStatusSelectOption[] {
    if (typeof body !== 'string' || !body) return [];
    try {
      const parsed = JSON.parse(body) as {
        success?: boolean;
        data?: Array<{ id: string; text?: string; label?: string; name?: string }>;
      };
      if (!parsed || parsed.success !== true || !Array.isArray(parsed.data)) {
        return [];
      }
      return parsed.data.map((item) => ({
        value: item.id || '',
        label: item.text || item.label || item.name || '',
      }));
    } catch {
      return [];
    }
  }
}
